import { ReactNode } from "react";

import { FieldProps } from "../_shared/field";
import FieldLabel from "../_shared/field-label";
import FieldMessage from "../_shared/field-message";

export interface ReadOnlyFieldProps extends Pick<
  FieldProps,
  "label" | "info" | "infoOpen" | "hint" | "name"
> {
  /** The settled value. `null`, `undefined` and `""` fall back to `emptyText`. */
  value: ReactNode;
  /** Actions rendered at the right end of the label row. */
  labelActions?: ReactNode;
  /** Stand-in for an absent value. Defaults to an em dash. */
  emptyText?: string;
}

/**
 * A field whose value cannot be edited, laid out like one that can.
 *
 * A locked field has no control to draw its label, so surfaces that mix locked
 * and editable fields tend to grow a second, hand-rolled label system that sits
 * at a different height. This renders the same {@link FieldLabel} row every
 * control renders, above a value band occupying the same 40px a control
 * occupies, so a row mixing the two stays on one optical line.
 *
 * `name` is accepted for prop symmetry with the editable controls but is not
 * wired to `htmlFor`: there is no focusable control here, and a label pointing
 * at nothing is an accessibility defect rather than a convenience.
 */
export function ReadOnlyField({
  label,
  info,
  infoOpen,
  hint,
  value,
  labelActions,
  emptyText = "—",
}: ReadOnlyFieldProps) {
  const isEmpty = value === null || value === undefined || value === "";

  return (
    <div className="font-sans w-full">
      <FieldLabel
        label={label}
        info={info}
        infoOpen={infoOpen}
        labelActions={labelActions}
      />

      <div className="flex items-center min-h-10 text-sm font-semibold text-default">
        {isEmpty ? emptyText : value}
      </div>

      <FieldMessage hint={hint} />
    </div>
  );
}

export default ReadOnlyField;
